import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Trash2, X, ChevronDown, ChevronRight, ChevronsDown, ChevronsRight, ChevronsUpDown, Check, RotateCcw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { formatPrice, formatQty } from '@/lib/format';
import { getAllowedNextStatuses } from '@/lib/orderTransitions';
import OrderServicesSection from '@/components/OrderServicesSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductCombobox = ({ products, value, onChange, getProductNameWithStock, index }) => {
  const [open, setOpen] = useState(false);
  const selected = products.find(p => p.product_id === value);
  const selectedInfo = selected ? getProductNameWithStock(selected.product_id) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={`item-product-${index}`}
          className="w-full justify-between border-stone-200 font-normal h-9 text-sm"
        >
          {selectedInfo ? (
            <span className="truncate">{selectedInfo.name}</span>
          ) : (
            <span className="text-stone-500">Search & select product...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
        <Command>
          <CommandInput placeholder="Type to search product..." />
          <CommandList className="max-h-[200px] overflow-y-auto">
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {products.filter(p => p.is_active !== false || p.product_id === value).map((product) => {
                const info = getProductNameWithStock(product.product_id);
                return (
                  <CommandItem
                    key={product.product_id}
                    value={`${product.product_name} ${product.product_brand} ${product.product_size} ${product.product_color} ${product.category}`}
                    onSelect={() => {
                      onChange(product.product_id);
                      setOpen(false);
                      setTimeout(() => {
                        const qtyInput = document.querySelector(`[data-testid="order-qty-input-${index}"]`);
                        if (qtyInput) { qtyInput.focus(); qtyInput.select(); }
                      }, 50);
                    }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === product.product_id ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="flex-1 truncate">{info.name}</span>
                    <span className="font-semibold text-stone-700 text-xs ml-2 whitespace-nowrap" data-testid={`product-price-${product.product_id}`}>{info.priceText}</span>
                    <span className={`font-semibold ${info.stockColor} text-xs ml-2 whitespace-nowrap`}>({info.stockText})</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('project_id');

  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [orderItems, setOrderItems] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [allExpanded, setAllExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [flushing, setFlushing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState('form'); // 'form' | 'services'
  const [orderType, setOrderType] = useState('products'); // 'products' | 'services' | 'combined'
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [formData, setFormData] = useState({
    project_id: projectIdParam || '',
    order_date: new Date().toISOString().split('T')[0],
    items: [{ product_id: '', quantity: 1, remarks: '' }]
  });

  useEffect(() => {
    fetchData();
  }, [projectIdParam]);

  const fetchData = async () => {
    try {
      const projectsRes = await axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true });
      const productsRes = await axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true });
      const inventoryRes = await axios.get(`${BACKEND_URL}/api/inventory`, { withCredentials: true });
      const vendorsRes = await axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true });
      setProjects(projectsRes.data);
      setProducts(productsRes.data);
      setInventory(inventoryRes.data);
      setVendors(vendorsRes.data);

      const ordersUrl = projectIdParam
        ? `${BACKEND_URL}/api/orders?project_id=${projectIdParam}`
        : `${BACKEND_URL}/api/orders`;
      const ordersRes = await axios.get(ordersUrl, { withCredentials: true });
      setOrders(ordersRes.data);

      const itemsData = {};
      const expandedState = {};
      for (const order of ordersRes.data) {
        try {
          const itemsRes = await axios.get(`${BACKEND_URL}/api/order-items/${order.order_id}`, { withCredentials: true });
          itemsData[order.order_id] = itemsRes.data;
          expandedState[order.order_id] = true;
        } catch (error) {
          itemsData[order.order_id] = [];
          expandedState[order.order_id] = true;
        }
      }
      setOrderItems(itemsData);
      setExpandedOrders(expandedState);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id) { toast.error('Select a project'); return; }
    const validItems = orderType === 'services'
      ? []
      : formData.items.filter(i => i.product_id && i.quantity > 0);
    if (orderType === 'products' && validItems.length === 0) {
      toast.error('Add at least one product, or switch Order Type to "Contract / Services only"');
      return;
    }
    try {
      const res = await axios.post(`${BACKEND_URL}/api/orders`, { ...formData, items: validItems }, { withCredentials: true });
      const newId = res.data?.order_id;
      toast.success(orderType === 'products' ? 'Order created' : 'Order created — add services below');
      setCreatedOrderId(newId);
      setDialogStep('services');
      // Refresh so the new order appears in the list behind the dialog when we close
      fetchData();
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const closeOrderDialog = () => {
    setDialogOpen(false);
    setDialogStep('form');
    setOrderType('products');
    setCreatedOrderId(null);
    resetForm();
    fetchData();
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/orders/${orderId}`, { withCredentials: true });
      toast.success('Order deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const flushPendingWhatsApp = async () => {
    setFlushing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/notification-settings/flush-batches`, {}, { withCredentials: true });
      const n = res.data?.flushed ?? 0;
      if (n > 0) {
        toast.success(`Sent ${n} pending WhatsApp digest${n === 1 ? '' : 's'} now`);
      } else {
        toast.info('No pending WhatsApp digests to send');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to flush pending notifications');
    } finally {
      setFlushing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: projectIdParam || '',
      order_date: new Date().toISOString().split('T')[0],
      items: [{ product_id: '', quantity: 1, remarks: '' }]
    });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, { product_id: '', quantity: 1, remarks: '' }] });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'product_id' && value && index === newItems.length - 1) {
      newItems.push({ product_id: '', quantity: 1, remarks: '' });
    }
    setFormData({ ...formData, items: newItems });
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || 'Unknown';
  };

  const getProductDetails = (productId) => {
    return products.find(p => p.product_id === productId);
  };

  const getProductStock = (productId) => {
    const inventoryItem = inventory.find(i => i.product_id === productId);
    return inventoryItem?.current_stock || 0;
  };

  const getProductNameWithStock = (productId) => {
    const product = products.find(p => p.product_id === productId);
    if (!product) return { name: 'Unknown', stockText: '0', stockColor: 'text-red-600', priceText: '' };
    const stock = getProductStock(productId);
    const sp = Number(product.selling_price || 0);
    return {
      name: `${product.product_name} - ${product.product_brand} - ${product.product_size} - ${product.product_color}`,
      stockText: `Stock: ${formatQty(stock)}`,
      stockColor: stock > 0 ? 'text-lime-700' : 'text-red-700',
      priceText: `₹${formatPrice(sp)}`
    };
  };

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const toggleAllOrders = () => {
    const newState = !allExpanded;
    const newExpandedState = {};
    orders.forEach(order => { newExpandedState[order.order_id] = newState; });
    setExpandedOrders(newExpandedState);
    setAllExpanded(newState);
  };

  const [qtyDialog, setQtyDialog] = useState({ open: false, orderId: '', itemId: '', status: '', qty: 0, ordered: 0 });

  const handleStatusChange = async (orderId, orderItemId, newStatus, item) => {
    const QTY_STATUSES = ['Dispatched from Vendor', 'Received in Store', 'Delivered'];
    if (QTY_STATUSES.includes(newStatus)) {
      // Pre-fill with sensible default: dispatched→ordered, received→dispatched or ordered, delivered→received
      let defaultQty = item.quantity;
      if (newStatus === 'Received in Store') defaultQty = item.dispatched_qty || item.quantity;
      if (newStatus === 'Delivered') defaultQty = item.received_qty || item.quantity;
      setQtyDialog({ open: true, orderId, itemId: orderItemId, status: newStatus, qty: defaultQty, ordered: item.quantity });
      return;
    }
    try {
      await axios.patch(
        `${BACKEND_URL}/api/order-items/${orderItemId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Status updated successfully');
      const itemsRes = await axios.get(`${BACKEND_URL}/api/order-items/${orderId}`, { withCredentials: true });
      setOrderItems(prev => ({ ...prev, [orderId]: itemsRes.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const confirmQtyStatus = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/order-items/${qtyDialog.itemId}/status`,
        { status: qtyDialog.status, actual_qty: qtyDialog.qty },
        { withCredentials: true }
      );
      toast.success('Status updated successfully');
      const itemsRes = await axios.get(`${BACKEND_URL}/api/order-items/${qtyDialog.orderId}`, { withCredentials: true });
      setOrderItems(prev => ({ ...prev, [qtyDialog.orderId]: itemsRes.data }));
      setQtyDialog({ open: false, orderId: '', itemId: '', status: '', qty: 0, ordered: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const [editingQty, setEditingQty] = useState({ itemId: null, orderId: null, field: null, value: '' });

  const handleQtyEdit = async (itemId, orderId, field, newValue) => {
    const val = parseFloat(newValue);
    if (isNaN(val) || val < 0) { toast.error('Invalid quantity'); return; }
    try {
      await axios.patch(`${BACKEND_URL}/api/order-items/${itemId}/edit-qty`, { field, value: val }, { withCredentials: true });
      toast.success('Quantity updated');
      setEditingQty({ itemId: null, orderId: null, field: null, value: '' });
      const itemsRes = await axios.get(`${BACKEND_URL}/api/order-items/${orderId}`, { withCredentials: true });
      setOrderItems(prev => ({ ...prev, [orderId]: itemsRes.data }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleReorder = async (orderId, itemId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/order-items/${itemId}/reorder`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reorder');
    }
  };

  const getItemStatusBadge = (status) => {
    const styles = {
      'Pending': 'bg-stone-100 text-stone-800 border-stone-200',
      'Delivered': 'bg-lime-100 text-lime-800 border-lime-200',
      'Ordered from Vendor': 'bg-blue-100 text-blue-800 border-blue-200',
      'Ready for Delivery': 'bg-amber-100 text-amber-800 border-amber-200',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800 border-amber-200',
      'Received in Store': 'bg-lime-100 text-lime-800 border-lime-200',
      'Cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || styles['Pending']}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="text-stone-600">Loading orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Orders
          </h1>
          <p className="mt-1 text-xs text-stone-600">
            {projectIdParam ? `Orders for ${getProjectName(projectIdParam)}` : `${orders.length} orders`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="flush-whatsapp-btn"
            onClick={flushPendingWhatsApp}
            disabled={flushing}
            variant="outline"
            size="sm"
            className="border-green-200 text-green-700 hover:bg-green-50 text-xs h-8"
            title="Send any queued WhatsApp digests to vendors/customers right now (skip the 5-min batch window)"
          >
            <Send className="w-3 h-3 mr-1" /> {flushing ? 'Sending…' : 'Send WhatsApp Now'}
          </Button>
          <Button
            data-testid="toggle-all-btn"
            onClick={toggleAllOrders}
            variant="outline"
            size="sm"
            className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs h-8"
          >
            {allExpanded ? (
              <><ChevronsRight className="w-3 h-3 mr-1" /> Collapse All</>
            ) : (
              <><ChevronsDown className="w-3 h-3 mr-1" /> Expand All</>
            )}
          </Button>
          <Button
            data-testid="create-order-btn"
            onClick={() => setDialogOpen(true)}
            size="sm"
            className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8"
          >
            <Plus className="w-3 h-3 mr-1" /> Create Order
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) closeOrderDialog(); else setDialogOpen(v); }}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogStep === 'form' ? 'Create New Order' : 'Add Services (optional)'}</DialogTitle>
          </DialogHeader>
          {dialogStep === 'form' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="project_id">Project *</Label>
              <Select value={formData.project_id} onValueChange={(value) => setFormData({ ...formData, project_id: value })} required>
                <SelectTrigger data-testid="order-project-select" className="border-stone-200"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>{project.project_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="order_date">Order Date *</Label>
              <Input id="order_date" type="date" value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })} required className="border-stone-200" />
            </div>
            <div>
              <Label>Order Type</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {[
                  { key: 'products', label: 'Material Supply', desc: 'Catalog products' },
                  { key: 'services', label: 'Contract / Services', desc: 'Labor, glass, etc.' },
                  { key: 'combined', label: 'Combined / Turnkey', desc: 'Products + services' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`order-type-${t.key}`}
                    onClick={() => setOrderType(t.key)}
                    className={`rounded-md border px-3 py-2 text-left transition ${orderType === t.key ? 'border-amber-700 bg-amber-50' : 'border-stone-200 bg-white hover:bg-stone-50'}`}
                  >
                    <div className={`text-xs font-semibold ${orderType === t.key ? 'text-amber-900' : 'text-stone-700'}`}>{t.label}</div>
                    <div className="text-[10px] text-stone-500">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            {orderType !== 'services' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Order Items <span className="text-xs font-normal text-stone-500">(catalog products)</span></Label>
                <Button type="button" onClick={addItem} size="sm" variant="outline" data-testid="add-item-btn"><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
              </div>
              {formData.items.map((item, index) => (
                <div key={index} className="border border-stone-200 rounded-md p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-stone-700">Item {index + 1}</span>
                    {formData.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(index)} className="text-red-700 hover:text-red-800"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="md:col-span-2">
                      <Label>Product</Label>
                      <ProductCombobox
                        products={products}
                        value={item.product_id}
                        onChange={(value) => updateItem(index, 'product_id', value)}
                        getProductNameWithStock={getProductNameWithStock}
                        index={index}
                      />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input data-testid={`order-qty-input-${index}`} type="number" step="0.01" min="0.01" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))} required={!!item.product_id} className="border-stone-200" />
                    </div>
                    <div className="md:col-span-3">
                      <Label>Remarks</Label>
                      <Input value={item.remarks} onChange={(e) => updateItem(index, 'remarks', e.target.value)} placeholder="Optional notes" className="border-stone-200" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
            <Button type="submit" data-testid="submit-order-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
              {orderType === 'services' ? 'Save & Continue to Contract Lines →' : orderType === 'combined' ? 'Save & Continue to Services →' : 'Create Order'}
            </Button>
          </form>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-stone-600 bg-lime-50 border border-lime-200 rounded-md p-3">
                Order <strong>#{createdOrderId}</strong> created. Add any services (transport, labor, misc costs) below, or click <strong>Done</strong> to finish.
              </p>
              {createdOrderId && (
                <OrderServicesSection orderId={createdOrderId} vendors={vendors} />
              )}
              <div className="flex justify-end pt-2 border-t border-stone-200">
                <Button type="button" data-testid="done-order-dialog-btn" onClick={closeOrderDialog} className="bg-amber-700 hover:bg-amber-800 text-white">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 border-b border-stone-300">
              <tr>
                <th className="text-left p-2 font-semibold text-stone-700 w-8"></th>
                <th className="text-left p-2 font-semibold text-stone-700">ORDER ID</th>
                <th className="text-left p-2 font-semibold text-stone-700">PROJECT</th>
                <th className="text-left p-2 font-semibold text-stone-700">DATE</th>
                <th className="text-right p-2 font-semibold text-stone-700">ORDERED AMT</th>
                <th className="text-right p-2 font-semibold text-stone-700">DELIVERED AMT</th>
                <th className="text-right p-2 font-semibold text-stone-700">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-stone-500 text-xs">
                    No orders found. Create your first order to get started.
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const items = orderItems[order.order_id] || [];
                  // Delivered amount: only count items that have been Delivered, using the actually-delivered qty
                  const deliveredAmount = items.reduce((sum, it) => {
                    if (it.status !== 'Delivered') return sum;
                    const qty = (it.delivered_qty != null ? it.delivered_qty : it.quantity) || 0;
                    const unit = it.unit_selling_price != null
                      ? it.unit_selling_price
                      : (it.quantity ? (it.price || 0) / it.quantity : 0);
                    return sum + qty * unit;
                  }, 0);
                  const itemsLoaded = orderItems[order.order_id] !== undefined;
                  const fullyDelivered = itemsLoaded && items.length > 0 && items.every(i => i.status === 'Delivered');
                  return (
                  <React.Fragment key={order.order_id}>
                    <tr data-testid={`order-row-${order.order_id}`} className="hover:bg-stone-50 border-b border-stone-200">
                      <td className="p-2">
                        <button
                          data-testid={`toggle-order-${order.order_id}`}
                          onClick={() => toggleOrder(order.order_id)}
                          className="text-stone-700 hover:text-amber-700"
                        >
                          {expandedOrders[order.order_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-2 font-mono text-stone-600">{order.order_id}</td>
                      <td className="p-2 font-medium text-stone-900">{getProjectName(order.project_id)}</td>
                      <td className="p-2 text-stone-700 tabular-nums">{order.order_date}</td>
                      <td className="p-2 text-right text-stone-900 font-medium tabular-nums" data-testid={`order-ordered-amt-${order.order_id}`}>{formatPrice(order.total_amount)}</td>
                      <td className="p-2 text-right tabular-nums" data-testid={`order-delivered-amt-${order.order_id}`}>
                        {!itemsLoaded ? (
                          <span className="text-stone-300 text-[10px]">—</span>
                        ) : deliveredAmount > 0 ? (
                          <span className={fullyDelivered ? 'text-lime-700 font-semibold' : 'text-amber-700 font-medium'} title={fullyDelivered ? 'All items delivered' : 'Partially delivered'}>
                            {formatPrice(deliveredAmount)}
                          </span>
                        ) : (
                          <span className="text-stone-300">—</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <button data-testid={`delete-order-${order.order_id}`} onClick={() => handleDelete(order.order_id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                    {expandedOrders[order.order_id] && (
                      <tr>
                        <td colSpan="7" className="bg-stone-50 p-3">
                          {orderItems[order.order_id]?.length === 0 ? (
                            <p className="text-xs text-stone-500">No items in this order</p>
                          ) : (
                            <table className="w-full text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-stone-200">
                                  <th className="text-left p-2 font-semibold text-stone-600">Product</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-16">Ordered</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-16">Disp.</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-16">Rcvd.</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-16">Dlvd.</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-24">Unit Price</th>
                                  <th className="text-right p-2 font-semibold text-stone-600 w-16">Stock</th>
                                  <th className="text-left p-2 font-semibold text-stone-600 w-28">Status</th>
                                  <th className="text-left p-2 font-semibold text-stone-600 w-44">Update Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orderItems[order.order_id]?.map((item) => {
                                  const product = getProductDetails(item.product_id);
                                  const unitPrice = product?.selling_price || 0;
                                  const receivedQty = item.received_qty || 0;
                                  const isTerminal = ['Received in Store', 'Delivered', 'Cancelled'].includes(item.status);
                                  const hasShortfall = isTerminal && receivedQty > 0 && receivedQty < item.quantity;
                                  const shortfallQty = item.quantity - receivedQty;
                                  const alreadyReordered = orderItems[order.order_id]?.some(
                                    sibling => sibling.remarks === `Reorder: shortfall from ${item.order_item_id}`
                                  );
                                  return (
                                    <tr key={item.order_item_id} data-testid={`item-row-${item.order_item_id}`} className="border-b border-stone-100 hover:bg-white">
                                      <td className="p-2">
                                        <div className="font-medium text-stone-900">{item.product_name} - <strong>{item.product_brand}</strong></div>
                                        <div className="text-stone-500">{item.product_size} | {item.product_color}</div>
                                        {item.remarks && <div className="text-stone-400 text-[10px] mt-0.5">{item.remarks}</div>}
                                        {hasShortfall && (
                                          <div className="flex items-center gap-1.5 mt-1">
                                            <span data-testid={`shortfall-badge-${item.order_item_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                                              {shortfallQty} pending from vendor
                                            </span>
                                            {alreadyReordered ? (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-stone-100 text-stone-500 border border-stone-200">
                                                Reordered
                                              </span>
                                            ) : (
                                              <button
                                                data-testid={`reorder-btn-${item.order_item_id}`}
                                                onClick={() => handleReorder(order.order_id, item.order_item_id)}
                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                                              >
                                                <RotateCcw className="w-2.5 h-2.5" /> Reorder
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td className="p-2 text-right tabular-nums font-medium">{formatQty(item.quantity)}</td>
                                      <td className="p-2 text-right tabular-nums">{item.dispatched_qty ? formatQty(item.dispatched_qty) : '-'}</td>
                                      <td className="p-2 text-right tabular-nums">
                                        {editingQty.itemId === item.order_item_id && editingQty.field === 'received_qty' ? (
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            autoFocus
                                            className="w-14 text-right text-xs border border-blue-300 rounded px-1 py-0.5 bg-blue-50"
                                            value={editingQty.value}
                                            onChange={(e) => setEditingQty({ ...editingQty, value: e.target.value })}
                                            onBlur={() => handleQtyEdit(item.order_item_id, order.order_id, 'received_qty', editingQty.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleQtyEdit(item.order_item_id, order.order_id, 'received_qty', editingQty.value); if (e.key === 'Escape') setEditingQty({ itemId: null, orderId: null, field: null, value: '' }); }}
                                          />
                                        ) : (
                                          <span
                                            className={`cursor-pointer hover:bg-blue-50 hover:text-blue-800 px-1 rounded ${item.received_qty ? '' : 'text-stone-400'}`}
                                            onClick={() => setEditingQty({ itemId: item.order_item_id, orderId: order.order_id, field: 'received_qty', value: item.received_qty || '' })}
                                            title="Click to edit received qty"
                                          >
                                            {item.received_qty ? formatQty(item.received_qty) : '-'}
                                          </span>
                                        )}
                                      </td>
                                      <td className="p-2 text-right tabular-nums">{item.delivered_qty ? formatQty(item.delivered_qty) : '-'}</td>
                                      <td className="p-2 text-right tabular-nums">{formatPrice(unitPrice)}</td>
                                      <td className="p-2 text-right tabular-nums">
                                        <span className={item.available_stock_quantity >= item.quantity ? 'text-lime-700 font-medium' : 'text-red-700 font-medium'}>
                                          {formatQty(item.available_stock_quantity)}
                                        </span>
                                      </td>
                                      <td className="p-2">{getItemStatusBadge(item.status)}</td>
                                      <td className="p-2">
                                        {(() => {
                                          const options = getAllowedNextStatuses(item);
                                          if (options.length <= 1) {
                                            // Terminal status (Delivered/Cancelled) — show static label, no dropdown
                                            return <span className="text-[10px] text-stone-400 italic px-2">{item.status}</span>;
                                          }
                                          return (
                                            <Select value={item.status} onValueChange={(value) => handleStatusChange(order.order_id, item.order_item_id, value, item)}>
                                              <SelectTrigger data-testid={`status-select-${item.order_item_id}`} className="w-full border-stone-200 text-xs h-7">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {options.map((status) => (
                                                  <SelectItem key={status} value={status} className="text-xs">{status}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          <OrderServicesSection orderId={order.order_id} vendors={vendors} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quantity confirmation dialog */}
      <Dialog open={qtyDialog.open} onOpenChange={(open) => { if (!open) setQtyDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Enter Actual Quantity</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Status: <span className="font-semibold text-stone-900">{qtyDialog.status}</span>
            </p>
            <p className="text-sm text-stone-600">
              Ordered: <span className="font-semibold text-stone-900">{qtyDialog.ordered}</span>
            </p>
            <div>
              <Label>Actual Quantity</Label>
              <Input
                data-testid="qty-dialog-input"
                type="number"
                step="0.01"
                min="0"
                value={qtyDialog.qty}
                onChange={(e) => setQtyDialog(prev => ({ ...prev, qty: parseFloat(e.target.value) || 0 }))}
                className="border-stone-200"
                autoFocus
              />
            </div>
            <Button data-testid="qty-dialog-confirm" onClick={confirmQtyStatus} className="w-full bg-amber-700 hover:bg-amber-800 text-white">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
